/**
 *  Sagas for the ResourceNames resource managment using Swagger client
 *
 * Each saga watcher intercepts a trigger action, does the asyncrhonous work in the respective worker saga and dispatches a success or a failure action.
 * Fetch calls are made via the swagger tags interface requests
 *
 */

import { apiClient, applyHeaders, buildHeaders } from "@bkry/bowline-utils";
import { ethers } from "ethers";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { getCachedPayload } from "../cachedPayload";
import { actions as contractsActions } from "../contracts";
import { storeIncluded } from "../dictionarySagas";
import { actions } from "./index";

export function* list(action) {
  const locale = yield select((state) => state.i18nState.lang);
  let headers = yield buildHeaders(locale);
  let {
    projectId,
    contract_address,
    pageNum,
    itemNum,
    sorting,
    filters,
    address,
  } = action.payload;

  const apiRequest = function* () {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.listMintTiersForProject,
      {
        project_id: projectId,
        address: contract_address,
        page: pageNum,
        items: itemNum,
        sorting: JSON.stringify(sorting),
        filters: JSON.stringify(filters),
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    return payload;
  };

  const successActions = function* (payload) {
    if (payload.obj.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.listSuccess(payload));
  };

  const failActions = function* (e) {
    yield put(actions.listFail({ message: e.message, ...e }));
  };

  yield* getCachedPayload(
    "mintTiers",
    "list",
    apiRequest,
    successActions,
    failActions
  );
}

export function* listTiersForProject(action) {
  const locale = yield select((state) => state.i18nState.lang);
  let headers = yield buildHeaders(locale);
  let { projectId } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.listMintTiersForProject,
      {
        project_id: projectId,
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    if (payload.obj.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.listSuccess(payload));
  } catch (e) {
    yield put(actions.listFail({ message: e.message, ...e }));
  }
}

/** Show Saga
 *  @description: connects to the showResourceName operation
 *  @param {number} action.payload the ResourceName id
 */
export function* show(action) {
  const { tierId, address, projectId } = action.payload;
  const locale = yield select((state) => state.i18nState.lang);
  let headers = yield buildHeaders(locale);

  const apiRequest = function* () {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.showMintTierForContract,
      {
        id: tierId,
        project_id: projectId,
        address: address,
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    return payload;
  };

  const successActions = function* (payload) {
    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.showSuccess(payload));
  };

  const failActions = function* (e) {
    yield put(actions.showFail({ message: e.message, ...e }));
  };

  yield* getCachedPayload(
    "mintTiers",
    "show",
    apiRequest,
    successActions,
    failActions
  );
}

/** Show Saga
 *  @description: connects to the showResourceName operation
 *  @param {number} action.payload the ResourceName id
 */
export function* generateWertSignature(action) {
  const {
    mint_tier_id,
    commodity_amount,
    contract_address,
    project_id,
    function_params,
    callbackFnc,
    function_abi,
  } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.createWertSignature,
      {
        mint_tier_id,
        project_id,
        commodity_amount,
        contract_address,
        function_params,
        function_abi,
      },
      { requestInterceptor: applyHeaders(headers) }
    );
    if (callbackFnc) callbackFnc(payload.obj);
  } catch (e) {}
}

/** Validate Voucher Code Saga
 *  @description: connects to the showResourceName operation
 *  @param {number} action.payload the ResourceName id
 */
export function* validate(action) {
  const { mint_tier_id, contract_address, project_id, voucher, callbackFnc } =
    action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.validateMintTierOnContract,
      {
        id: mint_tier_id,
        project_id,
        address: contract_address,
      },
      { requestInterceptor: applyHeaders(headers), requestBody: voucher }
    );
    if (callbackFnc) callbackFnc(payload.obj);
    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.validateSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.validateFail({ message: e.message, ...e }));
  }
}

/** Mint on MintTier Saga
 *  @description: connects to the showResourceName operation
 *  @param {number} action.payload the ResourceName id
 */
export function* mint(action) {
  const {
    mint_tier_id,
    contract_address,
    project_id,
    function_params,
    additionalBody,
    callbackFnc,
    provider,
    transactionCallbackFnc,
  } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.MintTiers.mintMintTierOnContract,
      {
        id: mint_tier_id,
        project_id,
        address: contract_address,
      },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody: { function_params, ...additionalBody },
      }
    );
    if (callbackFnc) callbackFnc(payload.obj);
    const ethersProvider =
      provider || new ethers.providers.Web3Provider(window.ethereum, "any");
    if (payload.obj?.data?.attributes?.bowline_relay_transaction_id) {
      yield put(actions.setWaitingTransactionConfirmation(true));
      ethersProvider.once(
        payload.obj?.data.attributes.bowline_relay_transaction_id,
        (transaction) => {
          // Emitted when the transaction has been mined
          if (transactionCallbackFnc) transactionCallbackFnc(transaction);
        }
      );
    }
    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.mintSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.mintFail({ message: e.message, ...e }));
  }
}

export function* mintProgressReset(action) {
  yield put(actions.setWaitingTransactionConfirmation(false));
}

export function* create(action) {
  let headers = yield buildHeaders();
  let { address, project_id, ...requestBody } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.MintTiers.createMintTierForContract,
      { project_id: project_id, address: address },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.createSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.createFail({ message: e.message, ...e }));
  }
}

export function* update(action) {
  const { project_id, tierId, address, ...requestBody } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.MintTiers.updateMintTiersForContract,
      { project_id: project_id, id: tierId, address: address },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody,
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });

    yield put(actions.updateSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.updateFail({ message: e.message, ...e }));
  }
}

/**
 * Saga Watchers
 * The exported list of sagas registered. When one of the action types is dispatched
 * the related worker saga is invoked.
 * Each saga is executed in a different thread
 */
function* mintTierSaga() {
  yield takeEvery(actions.create, create);
  yield takeEvery(actions.validate, validate);
  yield takeEvery(actions.mint, mint);
  yield takeEvery(contractsActions.mintProgressReset, mintProgressReset);
  yield takeEvery(actions.list, list);
  yield takeEvery(actions.generateWertSignature, generateWertSignature);
  yield takeEvery(actions.listTiersForProject, listTiersForProject);
  yield takeEvery(actions.show, show);
  yield takeEvery(actions.update, update);
}
export default mintTierSaga;
